/**
 * Klaret Client-Portal Shell — single-context client navigation.
 *
 * Topbar (56 px, --tvl-canvas 92% + blur): logo wordmark + "Client" badge,
 *   centred search (opens the GlobalCommandPalette on Ctrl-K), notification bell
 *   (placeholder) + UserMenu.
 * Sidebar: 264 px expanded / 64 px rail (hover-expand). Drawer below 768 px.
 * Mobile: BottomTabBar for the primary destinations.
 * Active item: fill icon + body-medium + --tvl-selected + 2 px claret inset.
 *
 * Constraints: no Lucide, no Radix, no #fff/#000, role/Klaret tokens only.
 */
import * as React from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppShell,
  AppShellMobileMenuButton,
  useAppShell,
  LogoWordmark,
  Sidebar,
  SidebarGroup,
  SidebarItem,
  UserMenu,
  Breadcrumbs,
  BottomTabBar,
  type BottomTabItem,
  type BreadcrumbItem,
  NotificationLine,
  Search2Line,
} from '@teivor/shared-ui';

import { applyTheme, getTheme, applyLocale, type Theme } from '../lib/theme';
import { RouteProgress, PageLoader } from '../components/RouteProgress';
import { PageErrorBoundary } from '../components/PageErrorBoundary';
import { GlobalCommandPalette } from '../components/GlobalCommandPalette';
import { activeNavItem, isNavItemActive, navItemsForRole, type PortalNavItem } from '../lib/nav';
import { useAccountQuery } from '../routes/portal/queries';
import { isRep, useSession } from '../lib/session';

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function NavSidebar({ pathname, navItems }: { pathname: string; navItems: PortalNavItem[] }) {
  const { t } = useTranslation();
  const { railExpanded } = useAppShell();
  const navigate = useNavigate();

  return (
    <Sidebar collapsed={!railExpanded}>
      <SidebarGroup>
        {navItems.map((item) => {
          const active = isNavItemActive(item, pathname);
          const Icon = active ? item.glyph.fill : item.glyph.line;
          return (
            <SidebarItem
              key={item.id}
              to={item.to}
              label={t(item.labelKey)}
              icon={<Icon size={20} color="currentColor" aria-hidden />}
              active={active}
              onClick={(e) => {
                // SidebarItem renders <a href> — intercept for SPA navigation.
                e.preventDefault();
                navigate(item.to);
              }}
            />
          );
        })}
      </SidebarGroup>
    </Sidebar>
  );
}

// ─── Topbar ──────────────────────────────────────────────────────────────────

function ShellTopbar() {
  const navigate = useNavigate();
  const { i18n: i18nInst, t } = useTranslation();
  const profileQ = useAccountQuery();

  // Theme switching — the portal previously had none. Mirrors the admin shell.
  const [theme, setTheme] = React.useState<Theme>(() => getTheme());
  const handleThemeChange = React.useCallback((t: Theme) => {
    applyTheme(t);
    setTheme(t);
  }, []);

  const profile = profileQ.data;
  const displayName = profile?.displayName || t('client.label', 'Client');
  const initials =
    (profile?.displayName ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || 'C';

  return (
    <div
      className="flex h-14 w-full items-center gap-3 px-3 md:px-4"
      style={{ fontFamily: 'var(--tvl-font-ui)' }}
    >
      {/* Mobile menu button */}
      <AppShellMobileMenuButton />

      {/* Logo + context badge */}
      <NavLink
        to="/"
        aria-label="Teivor"
        // See the admin shell: data-pop is what the mobile touch-target rule
        // keys on for links that act as controls.
        data-pop
        className="flex shrink-0 items-center gap-2 rounded-[var(--tvl-r1)] outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]"
      >
        <LogoWordmark className="h-7 w-auto text-[var(--tvl-ink)]" />
      </NavLink>
      <span className="hidden md:inline-flex shrink-0 items-center rounded-full bg-[var(--tvl-selected)] px-2 py-0.5 text-[10px] font-[600] uppercase tracking-wider text-[var(--tvl-accent)]">
        {t('client.label', 'Client')}
      </span>

      {/* Search — centred, opens the command palette */}
      <div className="mx-2 hidden flex-1 max-w-sm md:flex">
        <label className="sr-only" htmlFor="portal-search">
          {t('nav.search', 'Search')}
        </label>
        <button
          id="portal-search"
          type="button"
          aria-label={t('nav.search', 'Search')}
          onClick={() => {
            document.dispatchEvent(
              new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }),
            );
          }}
          className={[
            'flex h-9 w-full items-center gap-2 rounded-[var(--tvl-r2)]',
            'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
            'px-3 text-[14px] leading-[20px] text-[var(--tvl-ink-3)]',
            'hover:border-[var(--tvl-ink-2)] transition-colors duration-[var(--tvl-d-fast)]',
            'outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
          ].join(' ')}
        >
          <Search2Line size={16} color="currentColor" aria-hidden />
          <span className="flex-1 text-left">{t('nav.search_placeholder', 'Search…')}</span>
          <kbd
            className="hidden rounded-[var(--tvl-r1)] border border-[var(--tvl-hairline)] px-1 text-[11px] leading-[14px] text-[var(--tvl-ink-3)] lg:inline-flex"
            style={{ fontFamily: 'var(--tvl-font-mono)' }}
          >
            Ctrl K
          </kbd>
        </button>
      </div>

      <div className="ml-auto flex items-center gap-1">
        {/* Notification bell (placeholder) */}
        <button
          type="button"
          aria-label={t('nav.notifications', 'Notifications')}
          className={[
            'inline-flex h-9 w-9 items-center justify-center rounded-[var(--tvl-r2)]',
            'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
            'transition-colors duration-[var(--tvl-d-fast)]',
            'outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
          ].join(' ')}
        >
          <NotificationLine size={20} color="currentColor" aria-hidden />
        </button>

        {/* User menu */}
        <UserMenu
          name={displayName}
          email={profile?.email}
          initials={initials}
          theme={theme}
          onThemeChange={handleThemeChange}
          language={i18nInst.language}
          languages={[
            { code: 'cs', label: 'Čeština' },
            { code: 'en', label: 'English' },
          ]}
          onLanguageChange={(code) => applyLocale(code === 'en' ? 'en' : 'cs')}
          onProfile={() => navigate('/profile')}
          onSignOut={() => {
            void fetch('/api/iam/auth/logout', { method: 'POST', credentials: 'include' }).finally(
              () => navigate('/login'),
            );
          }}
        />
      </div>
    </div>
  );
}

// ─── Mobile bottom tab bar ─────────────────────────────────────────────────────

function MobileTabs({ pathname, navItems }: { pathname: string; navItems: PortalNavItem[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const items: BottomTabItem[] = navItems.map((item) => {
    const active = isNavItemActive(item, pathname);
    const Icon = active ? item.glyph.fill : item.glyph.line;
    return {
      id: item.id,
      label: t(item.labelKey),
      icon: <Icon size={20} color="currentColor" aria-hidden />,
      to: item.to,
      onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        navigate(item.to);
      },
    };
  });

  const activeId = navItems.find((i) => isNavItemActive(i, pathname))?.id ?? navItems[0]?.id ?? 'dashboard';

  return <BottomTabBar items={items} activeId={activeId} />;
}

// ─── AppLayout root ────────────────────────────────────────────────────────────

export function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  // Role drives both the navigation and the route-away guard. httpOnly cookies
  // are not JS-readable, so the role comes from /api/iam/auth/me.
  const { data: session, isLoading: sessionLoading } = useSession();
  const role = session?.role;
  const rep = isRep(role);
  const navItems = navItemsForRole(role);

  const isRoot = pathname === '/' || pathname === '/leads';
  // The chat is a full-height pane (fills the viewport, composer pinned at the
  // bottom); a breadcrumb row above it steals height and makes it overflow.
  const isChat = pathname === '/chat';
  const section = React.useMemo(() => activeNavItem(navItems, pathname), [navItems, pathname]);

  // Route-away guard (spec §Portal shell): a rep on a client-only page → My
  // leads; a client on a rep page → Dashboard. Only once the role is known.
  const repPath = pathname === '/leads' || pathname.startsWith('/leads/');
  const clientOnlyPath =
    pathname === '/' ||
    pathname === '/chat' ||
    pathname === '/approvals' ||
    pathname.startsWith('/approvals/');
  const redirectTo =
    sessionLoading || !role ? null : rep && clientOnlyPath ? '/leads' : !rep && repPath ? '/' : null;

  const root = navItems[0]!;
  const crumbs = React.useMemo<BreadcrumbItem[]>(
    () => [
      {
        label: t(root.labelKey),
        to: root.to,
        onClick: (e) => {
          e.preventDefault();
          navigate(root.to);
        },
      },
      ...(section.to !== root.to ? [{ label: t(section.labelKey) }] : []),
    ],
    [root, section, t, navigate],
  );

  return (
    <>
      <RouteProgress />
      <GlobalCommandPalette />
      <AppShell
        topbar={<ShellTopbar />}
        sidebar={<NavSidebar pathname={pathname} navItems={navItems} />}
        bottomTabs={<MobileTabs pathname={pathname} navItems={navItems} />}
      >
        {!isRoot && !isChat ? (
          <div className="pb-3">
            <Breadcrumbs items={crumbs} />
          </div>
        ) : null}
        <PageErrorBoundary routeKey={pathname}>
          <React.Suspense fallback={<PageLoader />}>
            {sessionLoading ? (
              <PageLoader />
            ) : redirectTo ? (
              <Navigate to={redirectTo} replace />
            ) : (
              <Outlet />
            )}
          </React.Suspense>
        </PageErrorBoundary>
      </AppShell>
    </>
  );
}
